import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../supabaseClient';

const Support = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    issue_type: '',
    subject: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Submit to Supabase
      const { data, error } = await supabase
        .from('support_tickets')
        .insert([{
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          issue_type: formData.issue_type,
          subject: formData.subject,
          description: formData.description,
          status: 'open',
          priority: 'normal',
          source: 'cargo_engage',
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      // Success
      setSubmitMessage('Your support request has been submitted successfully. We will respond within 24-48 hours.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        issue_type: '',
        subject: '',
        description: ''
      });
    } catch (error) {
      // Error handling
      setSubmitMessage('There was an error submitting your support request. Please email support@cargoengage.com or try again.');
      console.error('Supabase Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{paddingTop: '100px'}}>
      <Helmet>
        <title>Customer Support - Help & Contact Information | Cargo Engage</title>
        <meta name="description" content="Get help with lead generation services, SMS programs, or connecting with financial service providers. Email: support@cargoengage.com. Response within 24-48 hours." />
        <meta name="keywords" content="customer support, help center, contact support, SMS help, lead generation support" />
      </Helmet>
      <section className="section">
        <div className="container">
          <h1 className="section-title">Customer Support</h1>
          
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '3rem', alignItems: 'start'}}>
            <div>
              <h2 style={{color: '#1e3c72', marginBottom: '1.5rem'}}>Get Help</h2>
              <p style={{marginBottom: '2rem', lineHeight: '1.8'}}>
                Our support team is here to help you with any questions about our lead generation services, 
                SMS programs, or connecting with financial service providers.
              </p>

              <div style={{background: '#f0f9ff', padding: '2rem', borderRadius: '10px', marginBottom: '2rem', border: '2px solid #3b82f6'}}>
                <h3 style={{color: '#1e40af', marginBottom: '1rem'}}>Support Hours</h3>
                <p><strong>Business Hours:</strong></p>
                <p>Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                <p>Saturday - Sunday: Closed</p>
                <p style={{marginTop: '1rem', fontSize: '0.9rem'}}>
                  We respond to all support requests within 24-48 business hours.
                </p>
              </div>

              <div style={{background: '#f8fafc', padding: '2rem', borderRadius: '10px', marginBottom: '2rem'}}>
                <h3 style={{color: '#1e3c72', marginBottom: '1rem'}}>Contact Methods</h3>
                <p><strong>Email:</strong> support@cargoengage.com</p>
                <p><strong>General Inquiries:</strong> info@cargoengage.com</p>
                <p><strong>Company:</strong></p>
                <p><strong>CARGO AUTOMOTIVE SERVICES INC</strong><br />
                DBA: Cargo Engage<br />
                306 Schunnemunk Street<br />
                Monroe, NY 10950<br />
                United States</p>
                <p><strong>Owner:</strong> Mendel Friedman</p>
                <p><strong>EIN:</strong> 83-2229681</p>
              </div>

              <div style={{background: '#f0fdf4', padding: '2rem', borderRadius: '10px', border: '2px solid #16a34a'}}>
                <h3 style={{color: '#16a34a', marginBottom: '1rem'}}>Response Times</h3>
                <p><strong>Urgent Issues:</strong> Within 24 business hours</p>
                <p><strong>Standard Issues:</strong> Within 24-48 business hours</p>
                <p><strong>General Inquiries:</strong> Within 48 business hours</p>
              </div>
            </div>

            <div className="form-container">
              <h2 style={{color: '#1e3c72', marginBottom: '1.5rem'}}>Submit Support Request</h2>
              
              {submitMessage && (
                <div style={{
                  background: submitMessage.includes('error') ? '#fef2f2' : '#f0fdf4',
                  color: submitMessage.includes('error') ? '#ef4444' : '#16a34a',
                  padding: '1rem',
                  borderRadius: '5px',
                  marginBottom: '1.5rem',
                  border: `2px solid ${submitMessage.includes('error') ? '#ef4444' : '#16a34a'}`
                }}>
                  {submitMessage}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="issue_type">Issue Type *</label>
                  <select
                    id="issue_type"
                    name="issue_type"
                    value={formData.issue_type}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select issue type</option>
                    <option value="general">General Question</option>
                    <option value="sms">SMS Messages</option>
                    <option value="optout">Opt-Out Request</option>
                    <option value="privacy">Privacy Concern</option>
                    <option value="complaint">Complaint</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Brief description of your issue"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Detailed Description *</label>
                  <textarea
                    id="description"
                    name="description"
                    rows="6"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Please provide as much detail as possible..."
                    required
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Support Request'}
                </button>
              </form>
            </div>
          </div>

          <div style={{marginTop: '4rem'}}>
            <h2 style={{color: '#1e3c72', marginBottom: '2rem', textAlign: 'center'}}>Frequently Asked Questions</h2>
            
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem'}}>
              <div style={{background: '#f8fafc', padding: '1.5rem', borderRadius: '10px'}}>
                <h4 style={{color: '#1e3c72', marginBottom: '0.5rem'}}>How do I opt out of SMS messages?</h4>
                <p style={{fontSize: '0.95rem', lineHeight: '1.6'}}>
                  Reply STOP to any text message or visit our opt-out page. Requests are processed within 24 hours.
                </p>
              </div>

              <div style={{background: '#f8fafc', padding: '1.5rem', borderRadius: '10px'}}>
                <h4 style={{color: '#1e3c72', marginBottom: '0.5rem'}}>Who will contact me after I submit my information?</h4>
                <p style={{fontSize: '0.95rem', lineHeight: '1.6'}}>
                  Financial service providers and lending partners from our network may contact you about their products.
                </p>
              </div>

              <div style={{background: '#f8fafc', padding: '1.5rem', borderRadius: '10px'}}>
                <h4 style={{color: '#1e3c72', marginBottom: '0.5rem'}}>Is Cargo Engage a lender?</h4>
                <p style={{fontSize: '0.95rem', lineHeight: '1.6'}}>
                  No, we are a lead generation company. We connect consumers with financial service providers but do not make credit decisions.
                </p>
              </div>

              <div style={{background: '#f8fafc', padding: '1.5rem', borderRadius: '10px'}}>
                <h4 style={{color: '#1e3c72', marginBottom: '0.5rem'}}>How is my information protected?</h4>
                <p style={{fontSize: '0.95rem', lineHeight: '1.6'}}>
                  We use encryption and secure systems to protect your data. See our Privacy Policy for full details.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support;
